import { AlertBanner, AlertBannerStatus, darkColors, Footer, GlobalStyles, lightColors, NotificationContainer, SwitchThemeContext, theme } from '@tryhackme/thm-ui-components';
import { differenceInDays } from 'date-fns';
import { type ReactNode, useCallback, useContext, useEffect } from 'react';
import { matchRoutes, Outlet, useLocation, useNavigate, useNavigation } from 'react-router-dom';
import { useAppSelector } from 'src/app/hooks';
import { GoogleOneTap } from 'src/common/components/google-one-tap/google-one-tap';
import { useAnonymousSale } from 'src/common/contexts/anonymous-sale-context';
import { LanguageProvider } from 'src/common/contexts/language-context';
import { useLayoutOverride } from 'src/common/contexts/layout-override-context';
import { ModalType, type ThemeColorMode } from 'src/common/enums';
import { useFeatureIsOn } from 'src/common/growthbook';
import { useCurrentRoute, useCustomerIOIdentify, useHotjarIdentify, useIntercom, useScrollRestoration } from 'src/common/hooks';
import { useGrowthbookUpdateAttributes } from 'src/common/hooks/use-growthbook-update-attributes';
import { useReprofilingUser } from 'src/common/hooks/use-reprofiling-user';
import { useSegmentAnalytics } from 'src/common/hooks/use-segment-analytics';
import { StepperProvider } from 'src/common/hooks/use-stepper-modal';
import type { RouteHandleObject } from 'src/common/interfaces';
import { CampaignElementType } from 'src/common/interfaces/campaigns';
import { useGetUserQuery, useUpdateCardExpiryNotificationDismissalMutation } from 'src/common/slices';
import { CampaignElement } from 'src/features/campaigns/components/campaign-element';
import { useActiveCampaign } from 'src/features/campaigns/hooks/use-active-campaign';
import { CookieConsent } from 'src/features/cookie-consent';
import { MissionSocketManager } from 'src/features/dashboard/components/mission-socket-manager';
import { HeaderWrapper } from 'src/features/header';
import { ReprofilingModal } from 'src/features/reprofiling/components/reprofiling-modal/reprofiling-modal';
import { routes } from 'src/router/routes';
import { ThemeProvider } from 'styled-components';
import { RestrictedServiceModal } from '../restricted-service-modal';
import { SplitScreen } from '../split-screen/split-screen';
import { StyledMain } from './layout.styles';
import { shouldHideFooter } from './layout-utils';
const colors = {
  dark: darkColors,
  light: lightColors
};
declare global {
  interface Window {
    APP_LOADER_COLORS?: {
      dark?: string;
      light?: string;
    };
    APP_LOADER_TIMEOUT?: number;
    APP_LOADER_ELEMENT?: HTMLDivElement;
  }
}
interface LayoutProvidersProps {
  readonly forcedTheme?: keyof typeof colors;
  readonly children: ReactNode;
}
const createTheme = (themeColorMode: `${ThemeColorMode}`, forcedTheme?: keyof typeof colors) => ({
  ...theme,
  colors: forcedTheme ? colors[forcedTheme] : colors[themeColorMode]
});
export const LayoutProviders = ({
  forcedTheme,
  children
}: LayoutProvidersProps) => <SwitchThemeContext.Consumer data-sentry-element="unknown" data-sentry-component="LayoutProviders" data-sentry-source-file="layout.tsx">
    {({
    theme: themeColorMode
  }) => <LanguageProvider>
        <ThemeProvider theme={createTheme(themeColorMode, forcedTheme)}>
          <GlobalStyles />
          {children}
        </ThemeProvider>
      </LanguageProvider>}
  </SwitchThemeContext.Consumer>;
export const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const route = useCurrentRoute();
  const {
    data,
    isSuccess
  } = useGetUserQuery();
  const shouldSimplifyRooms = useFeatureIsOn('should-simplify-rooms');
  const {
    displayHeader: displayHeaderOverride
  } = useLayoutOverride();
  const {
    theme: currentTheme
  } = useContext(SwitchThemeContext);
  const userData = data?.data?.user;
  const expiringSubscriptionCardData = userData?.cardDetails;
  const cardExpiryDate = expiringSubscriptionCardData?.cardExpiryDate;
  const isCardExpiringSoon = expiringSubscriptionCardData?.isCardExpiringSoon;
  const [updateCardExpiryDimissalDate] = useUpdateCardExpiryNotificationDismissalMutation();
  const {
    campaign,
    shouldPromote
  } = useActiveCampaign();
  const anonymousSale = useAnonymousSale();
  const navigation = useNavigation();
  useEffect(() => {
    const loader = window.APP_LOADER_ELEMENT;
    if (!loader) return;

    // Clear any pending show timeout from initial load
    if (window.APP_LOADER_TIMEOUT) {
      clearTimeout(window.APP_LOADER_TIMEOUT);
      window.APP_LOADER_TIMEOUT = undefined;
    }
    if (navigation.state === 'idle') {
      loader.style.opacity = '0';
      // Restore loader background to current theme context
      if (window.APP_LOADER_COLORS?.[currentTheme]) {
        loader.style.backgroundColor = window.APP_LOADER_COLORS[currentTheme];
      }
      return;
    }
    if (navigation.state === 'loading') {
      // Get the destination route's handle if navigating
      if (navigation.location) {
        const matches = matchRoutes(routes, navigation.location);
        const destinationRoute = matches?.[matches.length - 1];
        const destinationHandle = destinationRoute?.route?.handle as RouteHandleObject | undefined;

        // Update loader background for forced theme
        if (destinationHandle?.forcedTheme && window.APP_LOADER_COLORS) {
          const bgColor = destinationHandle.forcedTheme === 'dark' ? window.APP_LOADER_COLORS.dark : window.APP_LOADER_COLORS.light;
          if (bgColor) loader.style.backgroundColor = bgColor;
        }
      }

      // Only show loader if navigation takes longer than 200ms
      const timeoutId = setTimeout(() => {
        loader.style.opacity = '1';
      }, 200);
      return () => clearTimeout(timeoutId);
    }
    return;
  }, [navigation.state, navigation.location, currentTheme]);
  useEffect(() => {
    const loader = window.APP_LOADER_ELEMENT;
    if (loader && window?.APP_LOADER_COLORS?.[currentTheme]) {
      loader.style.backgroundColor = window.APP_LOADER_COLORS[currentTheme];
    }
  }, [currentTheme]);
  const {
    hideFooter,
    forcedTheme,
    hideHeader,
    isHeaderTransparent = false,
    isHeaderSticky = true,
    headerDisplayNone = false,
    hideIntercom = false,
    isFullWidth = false
  } = route.handle || {};
  useIntercom(hideIntercom);
  useCustomerIOIdentify();
  useHotjarIdentify();
  useGrowthbookUpdateAttributes();
  useSegmentAnalytics();
  useScrollRestoration({
    elementId: 'split-screen-middle'
  });

  // Check if we should hide footer based on simplify rooms feature flag and user authentication
  // Only apply this logic to room routes (/room/:roomCode)
  const isRoomRoute = /^\/room\/[^/]+$/.test(location.pathname);
  const shouldHideFooterForSimplifyRooms = isRoomRoute && shouldSimplifyRooms && isSuccess;
  const showFooter = !hideFooter && !shouldHideFooter(location) && !shouldHideFooterForSimplifyRooms;

  // Use layout override if provided, otherwise use default logic
  const showHeader = displayHeaderOverride ?? !hideHeader;
  const handleDismissCardExpiryBanner = async () => {
    if (cardExpiryDate && isCardExpiringSoon) {
      const expiryDate = new Date(cardExpiryDate);
      const currentDate = new Date();
      const diffDays = differenceInDays(expiryDate, currentDate);
      await updateCardExpiryDimissalDate({
        lastCardExpiryDismissal: diffDays
      });
    }
  };
  const {
    currentModal
  } = useAppSelector(state => state.modalStates);
  const isRestrictedServiceModalOpen = currentModal === ModalType.RESTRICTED_SERVICE;
  const {
    reprofilingUsersIsEnabled,
    isReprofilingModalOpen,
    setIsReprofilingModalOpen
  } = useReprofilingUser(userData?.demographics?.lastReprofileAt);

  // This will log the error on first load even if the SEO component is present... need to fix
  // Checker to make sure <SEO/> is used on every page
  if (process.env.NODE_ENV === 'development') {
    // stable function reference to use as event listener
    // biome-ignore lint/correctness/useHookAtTopLevel: -- this is really "conditional" at runtime
    const checkSEOComponentPresence = useCallback(() => {
      // this would only be present if <SEO/> is used
      const metaImage = document.querySelector('meta[property="og:type"]');
      if (!metaImage) {
        // eslint-disable-next-line no-console
        console.error('<meta/> OpenGraph tags are not present!\nPlease make sure you are using the <SEO/> component on the page!');
      }
    }, []);
    // register event listener for hard page loads
    // biome-ignore lint/correctness/useHookAtTopLevel: -- this is really "conditional" at runtime
    useEffect(() => {
      window.addEventListener('load', checkSEOComponentPresence);
      return () => {
        window.removeEventListener('load', checkSEOComponentPresence);
      };
    }, [checkSEOComponentPresence]);
    // also run checker on location changes (hook above at the top) and any other re-renders
    // biome-ignore lint/correctness/useHookAtTopLevel: -- this is really "conditional" at runtime
    useEffect(() => {
      // do not run before page is fully loaded (those renders can not have the final <head/>)
      if (document.readyState === 'complete') checkSEOComponentPresence();
    });
  }
  return <LayoutProviders forcedTheme={forcedTheme} data-sentry-element="LayoutProviders" data-sentry-component="Layout" data-sentry-source-file="layout.tsx">
      <SplitScreen data-sentry-element="SplitScreen" data-sentry-source-file="layout.tsx">
        <CampaignElement type={CampaignElementType.TOP_BAR} data-sentry-element="CampaignElement" data-sentry-source-file="layout.tsx" />
        <CampaignElement type={CampaignElementType.MODAL} data-sentry-element="CampaignElement" data-sentry-source-file="layout.tsx" />
        {cardExpiryDate && isCardExpiringSoon && <AlertBanner status={AlertBannerStatus.ERROR} message="Your payment card will expire soon. Please update your card details to avoid any disruption to your subscription" type="button" buttonText="Update Payment Details" handleButtonOnClick={() => navigate('/manage-account/subscription?openModal=true')}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleBannerDismiss={handleDismissCardExpiryBanner} dismissable />}
        {showHeader && <HeaderWrapper isHeaderTransparent={isHeaderTransparent} isHeaderSticky={isHeaderSticky} headerDisplayNone={headerDisplayNone} stickToTop={(!campaign || !shouldPromote) && !anonymousSale.isActive} isFullWidth={isFullWidth} />}
        <StyledMain data-sentry-element="StyledMain" data-sentry-source-file="layout.tsx">
          <CampaignElement type={CampaignElementType.EMBEDDED_BAR} data-sentry-element="CampaignElement" data-sentry-source-file="layout.tsx" />
          <GoogleOneTap data-sentry-element="GoogleOneTap" data-sentry-source-file="layout.tsx" />
          <Outlet data-sentry-element="Outlet" data-sentry-source-file="layout.tsx" />
        </StyledMain>
        {showFooter && <Footer isAuth={isSuccess} />}
        <NotificationContainer data-sentry-element="NotificationContainer" data-sentry-source-file="layout.tsx" />
        <RestrictedServiceModal open={isRestrictedServiceModalOpen} data-sentry-element="RestrictedServiceModal" data-sentry-source-file="layout.tsx" />

        {reprofilingUsersIsEnabled && <StepperProvider>
            <ReprofilingModal open={isReprofilingModalOpen} onOpenChange={setIsReprofilingModalOpen} user={data?.data?.user} />
          </StepperProvider>}
      </SplitScreen>
      <CookieConsent data-sentry-element="CookieConsent" data-sentry-source-file="layout.tsx" />
      <MissionSocketManager data-sentry-element="MissionSocketManager" data-sentry-source-file="layout.tsx" />
    </LayoutProviders>;
};